import { useFocus } from '@e2m-customer-portal/hooks';
import { ClickAwayListener, Grow, IconButton, InputAdornment, Paper, TextField, TextFieldProps } from '@mui/material';
import React, { ReactElement, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui-compat';
import { CheckIcon, ClearIcon, EditIcon } from '../../assets/icons';

const useStyles = makeStyles()((theme) => ({
  createIconButton: {
    padding: theme.spacing(1),
  },
}));

export type SubmittableTextFieldProps = Omit<
  TextFieldProps,
  'value' | 'onSubmit' | 'onClick' | 'onChange' | 'InputProps'
> & {
  value?: string;
  onSubmit?: (value: string) => void;
  showPassword?: boolean;
  submitOnBlur?: boolean;
  clearPassword?: boolean;
  maxLength?: number;
  type?: React.HTMLInputTypeAttribute;
  color?: 'error' | 'info' | 'success' | 'warning' | 'primary' | 'secondary' | undefined;
};

export function SubmittableTextField({
  value: initialValue,
  onSubmit,
  color,
  type,
  submitOnBlur = false,
  showPassword = false,
  clearPassword = false,
  maxLength,
  ...props
}: Readonly<SubmittableTextFieldProps>): ReactElement<SubmittableTextFieldProps> {
  const { classes } = useStyles();
  const textFieldRef = useRef<HTMLDivElement | null>(null);
  const [t] = useTranslation();
  const [value, setValue] = useState(initialValue ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const { focus: setInputFocus, blur: blurInputFocus } = useFocus(inputRef.current);
  const [active, setActive] = useState(false);
  const [width, setWidth] = useState(NaN);
  const [textFieldType, setTextFieldType] = useState<React.HTMLInputTypeAttribute | undefined>(type);

  const handleOpen = useCallback((): void => {
    if (textFieldRef.current) {
      setWidth(textFieldRef.current?.clientWidth);
    }
    if (type === 'password') {
      if (showPassword) {
        setTextFieldType('text');
      }
      if (clearPassword) {
        setValue('');
      } else {
        setValue(value ?? '');
      }
    }
    setInputFocus();
    setActive(true);
  }, [setInputFocus, value, type, showPassword, clearPassword]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setValue(event.target.value);
  };

  const handleCancel = useCallback((): void => {
    blurInputFocus();
    setActive(false);
    setValue(initialValue ?? '');
    if (type === 'password' && showPassword) {
      setTextFieldType(type);
    }
  }, [blurInputFocus, initialValue, type, showPassword]);

  const handleSubmit = useCallback(() => {
    blurInputFocus();
    setActive(false);
    if (type === 'password' && showPassword) {
      setTextFieldType(type);
    }
    onSubmit?.(value ?? '');
  }, [blurInputFocus, onSubmit, value, type, showPassword]);

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (submitOnBlur) {
        event.preventDefault();
        handleSubmit();
      } else {
        handleCancel();
      }
    },
    [handleCancel, handleSubmit, submitOnBlur],
  );

  useLayoutEffect(() => {
    setValue(initialValue ?? '');
  }, [initialValue]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <form
        className="relative w-fit"
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <TextField
          ref={textFieldRef}
          variant="outlined"
          type={textFieldType}
          color={color}
          value={type === 'password' && !active ? t('SHARED_PASSWORD_PLACEHOLDER') : value}
          onClick={handleOpen}
          onFocus={handleOpen}
          style={(active && { width, backgroundColor: '#fff' }) || undefined}
          InputProps={{
            inputRef,
            endAdornment: !active && (
              <InputAdornment position="end" disablePointerEvents>
                <IconButton className={classes.createIconButton} tabIndex={-1}>
                  <EditIcon color={color} className="w-5" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={handleChange}
          {...props}
        />
        <Grow in={active && !submitOnBlur} className="absolute bottom-2 right-0 z-10 w-fit ">
          <Paper square className="p-0.5">
            <IconButton
              color="primary"
              className="m-[0.0625rem] rounded-sm border border-solid p-0.5"
              type="submit"
              tabIndex={-1}
              onClick={handleSubmit}
              aria-label={t('SHARED_SUBMIT')}
            >
              <CheckIcon />
            </IconButton>
            <IconButton
              color="error"
              className="m-[0.0625rem] rounded-sm border border-solid p-0.5"
              tabIndex={-1}
              onClick={handleCancel}
              aria-label={t('SHARED_CANCEL')}
            >
              <ClearIcon />
            </IconButton>
          </Paper>
        </Grow>
      </form>
    </ClickAwayListener>
  );
}
